import { Platform } from 'react-native';
import NetInfo from '@react-native-community/netinfo';

export type ConnectivityStatus = {
  isOnline: boolean;
  isInternetReachable: boolean;
  type: string | null;
};

function webNavigatorOnlineStatus(): ConnectivityStatus | null {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return null;
  }
  const online = navigator.onLine;
  return {
    isOnline: online,
    isInternetReachable: online,
    type: 'web',
  };
}

export async function getConnectivityStatus(): Promise<ConnectivityStatus> {
  const webStatus = webNavigatorOnlineStatus();
  if (webStatus) {
    return webStatus;
  }
  const state = await NetInfo.fetch();
  return {
    isOnline: Boolean(state.isConnected),
    isInternetReachable: state.isInternetReachable !== false,
    type: state.type ?? null,
  };
}

export function subscribeConnectivity(callback: (status: ConnectivityStatus) => void): () => void {
  const webStatus = webNavigatorOnlineStatus();
  if (webStatus && typeof window !== 'undefined') {
    const emit = () => {
      const next = webNavigatorOnlineStatus();
      if (next) callback(next);
    };
    window.addEventListener('online', emit);
    window.addEventListener('offline', emit);
    const netUnsub = NetInfo.addEventListener(() => {
      // En web manda `navigator.onLine`. NetInfo a veces sigue "connected" con DevTools offline.
      emit();
    });
    return () => {
      window.removeEventListener('online', emit);
      window.removeEventListener('offline', emit);
      netUnsub();
    };
  }
  return NetInfo.addEventListener((state) => {
    callback({
      isOnline: Boolean(state.isConnected),
      isInternetReachable: state.isInternetReachable !== false,
      type: state.type ?? null,
    });
  });
}

export async function isOnline(): Promise<boolean> {
  const status = await getConnectivityStatus();
  return status.isOnline && status.isInternetReachable;
}
